import { useRef, useState, useMemo, useEffect } from "react";
import Moveable from "react-moveable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ImagePicker from "@/components/ImagePicker";
import api from "@/lib/api";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/dialogs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Type, Heading, Image as ImageIcon, Square, Minus, Hash, Braces, Layers, Trash2, Copy, Table, Save, FolderOpen, Undo2, Redo2,
  Lock, Unlock, Eye, EyeOff, BringToFront, SendToBack,
  AlignHorizontalJustifyStart, AlignHorizontalJustifyCenter, AlignHorizontalJustifyEnd,
  AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd,
} from "lucide-react";

const PXMM = 2.55;               // editor pixels per millimetre
const A4 = { w: 210, h: 297 };   // mm
const CW = A4.w * PXMM, CH = A4.h * PXMM;
const fontPx = (pt) => (pt || 12) * 0.3528 * PXMM;
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Math.random()).slice(2));

const SAMPLE = {
  "client.company_name": "Adventure Graphics Limited", "client.contact_name": "Jane Smith",
  "client.trading_name": "Adventure Graphics", "proposal.reference": "CASS-PROP-1042",
  "proposal.date": "14 June 2026", "proposal.expiry_date": "30 June 2026",
  "engagement.start_date": "01 July 2026", "pricing.monthly_gross": "£312.00",
  "business.name": "Cloud Accounting Support Services", "business.website": "www.cass-online.co.uk",
  "page.number": "1", "page.total": "12",
};
const PLACEHOLDERS = Object.keys(SAMPLE).map((k) => [`{{${k}}}`, k]);

const TABLE_SOURCES = [["monthly", "Monthly fees"], ["quarterly", "Quarterly fees"], ["annual", "Annual fees"], ["one_off", "One-off costs"], ["services", "Services & inclusions"], ["catchup", "Catch-up fees"], ["payment_plan", "Payment plan"], ["factors", "Pricing factors"], ["toc", "Table of contents"], ["testimonials", "Testimonials"], ["packages", "Package comparison"]];
const SAMPLE_ROWS = {
  monthly: { head: ["Service", "Net", "VAT", "Gross"], rows: [["Bookkeeping", "£150.00", "£30.00", "£180.00"], ["Payroll", "£60.00", "£12.00", "£72.00"], ["VAT Returns", "£45.00", "£9.00", "£54.00"]] },
  quarterly: { head: ["Service", "Net", "VAT", "Gross"], rows: [["Management accounts", "£300.00", "£60.00", "£360.00"]] },
  annual: { head: ["Service", "Net", "VAT", "Gross"], rows: [["Year-end accounts", "£900.00", "£180.00", "£1,080.00"]] },
  one_off: { head: ["Service", "Net", "VAT", "Gross"], rows: [["Xero setup", "£250.00", "£50.00", "£300.00"]] },
  services: { head: ["Service", "What's included"], rows: [["Bookkeeping", "Bank recs, invoices, ledgers"], ["Payroll", "RTI, payslips, pensions"]] },
  catchup: { head: ["Name", "Catch-up period", "Fee"], rows: [["Bookkeeping catch-up", "Apr–Jun 2026", "£450.00"]] },
  payment_plan: { head: ["Description", "Due date", "Amount"], rows: [["Catch-up — instalment 1", "31 Jul 2026", "£301.00"], ["Catch-up — instalment 2", "31 Aug 2026", "£301.00"], ["Catch-up — instalment 3", "30 Sep 2026", "£301.00"]] },
  factors: { head: ["Factor", ""], rows: [["Turnover band", "£250k–£500k"], ["Transactions / month", "~180"]] },
  toc: { head: ["Contents"], rows: [["Introduction"], ["Pricing summary"], ["Service details"], ["Next steps"]] },
  testimonials: { head: ["What our clients say"], rows: [["\u201cCASS transformed our finances.\u201d — Jane, MD"], ["\u201cResponsive and proactive.\u201d — Tom, Founder"]] },
  packages: { head: ["Features", "Essential", "Growth"], rows: [["Bookkeeping", "\u2713", "\u2713"], ["Management accounts", "\u2013", "\u2713"], ["Payroll", "\u2013", "\u2713"]] },
};

const TablePreview = ({ e }) => {
  const s = e.style || {};
  const d = SAMPLE_ROWS[e.content?.source || "monthly"] || SAMPLE_ROWS.monthly;
  const fpx = Math.max(6, fontPx(s.font_size || 9));
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fpx, tableLayout: "fixed" }}>
      <thead><tr>{d.head.map((h, i) => <th key={i} style={{ background: s.header_bg || "#0B1A2F", color: s.header_fg || "#fff", textAlign: i === 0 ? "left" : "right", padding: "2px 5px", fontWeight: 700 }}>{h}</th>)}</tr></thead>
      <tbody>{d.rows.map((r, ri) => <tr key={ri} style={{ background: ri % 2 ? (s.alt_row || "#F1F5F9") : "#fff" }}>{r.map((c, ci) => <td key={ci} style={{ color: s.color || "#475569", textAlign: ci === 0 ? "left" : "right", padding: "2px 5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c}</td>)}</tr>)}</tbody>
    </table>
  );
};

const THMM = 90 / 210;
const BlockThumb = ({ elements = [], bg = "#fff" }) => (
  <div style={{ position: "relative", width: 90, height: 127, background: bg, border: "1px solid #E2E8F0", flexShrink: 0, overflow: "hidden" }}>
    {[...elements].sort((a, b) => (a.z || 0) - (b.z || 0)).map((e, i) => {
      const st = { position: "absolute", left: (e.x || 0) * THMM, top: (e.y || 0) * THMM, width: (e.w || 0) * THMM, height: (e.h || 0) * THMM };
      if (e.type === "shape") return <div key={i} style={{ ...st, background: e.style?.fill || "#2EA3FD", borderRadius: (e.style?.radius || 0) * THMM }} />;
      if (["image", "logo", "background"].includes(e.type)) return <div key={i} style={{ ...st, background: "#E2E8F0" }} />;
      if (e.type === "table") return <div key={i} style={{ ...st, background: "#F1F5F9", borderTop: `2px solid ${e.style?.header_bg || "#0B1A2F"}` }} />;
      if (e.type === "divider") return <div key={i} style={{ ...st, borderTop: `1px solid ${e.style?.color || "#CBD5E1"}` }} />;
      return <div key={i} style={{ ...st, background: e.style?.color || "#0B1A2F", opacity: 0.7, height: Math.max(2, (e.h || 4) * THMM * 0.5) }} />;
    })}
  </div>
);

const NEW = {
  heading: () => ({ type: "heading", x: 25, y: 40, w: 160, h: 24, content: { text: "Heading" }, style: { font_size: 30, bold: true, color: "#0B1A2F", align: "left", line_height: 1.1 } }),
  text: () => ({ type: "text", x: 25, y: 70, w: 160, h: 20, content: { text: "Your text here." }, style: { font_size: 12, color: "#0B1A2F", align: "left", line_height: 1.3 } }),
  placeholder: () => ({ type: "placeholder", x: 25, y: 100, w: 120, h: 12, content: { text: "{{client.company_name}}" }, style: { font_size: 14, bold: true, color: "#0B1A2F", align: "left" } }),
  image: () => ({ type: "image", x: 60, y: 120, w: 90, h: 60, content: { image_path: "", url: "" }, style: { opacity: 1, fit: "contain" } }),
  logo: () => ({ type: "logo", x: 25, y: 20, w: 55, h: 22, content: { image_path: "", url: "" }, style: { opacity: 1, fit: "contain" } }),
  shape: () => ({ type: "shape", x: 20, y: 200, w: 120, h: 50, style: { fill: "#2EA3FD", opacity: 1, radius: 3, border_width: 0, border_color: "#0B1A2F" } }),
  divider: () => ({ type: "divider", x: 25, y: 160, w: 120, h: 2, style: { color: "#CBD5E1", thickness: 1 } }),
  page_number: () => ({ type: "page_number", x: 130, y: 285, w: 60, h: 8, content: { text: "Page {{page.number}} of {{page.total}}" }, style: { font_size: 9, color: "#94A3B8", align: "right" } }),
  table: () => ({ type: "table", x: 20, y: 120, w: 170, h: 60, content: { source: "monthly" }, style: { header_bg: "#0B1A2F", header_fg: "#FFFFFF", alt_row: "#F1F5F9", color: "#475569", font_size: 9, show_totals: true } }),
  background: () => ({ type: "background", x: 0, y: 0, w: 210, h: 297, z: -1, content: { image_path: "", url: "" }, style: { opacity: 1, fit: "fill" } }),
};
const TOOLS = [
  ["heading", "Heading", Heading], ["text", "Text", Type], ["placeholder", "Placeholder", Braces],
  ["image", "Image", ImageIcon], ["logo", "Logo", ImageIcon], ["shape", "Shape", Square],
  ["divider", "Divider", Minus], ["table", "Table", Table], ["page_number", "Page number", Hash], ["background", "Background", Layers],
];

const PRESET_DEFS = {
  hero: (title, sub) => ({ bg_color: "#0B1A2F", elements: [
    { type: "logo", x: 20, y: 22, w: 52, h: 20, content: { image_path: "", url: "" }, style: { fit: "contain", opacity: 1 } },
    { type: "heading", x: 20, y: 150, w: 172, h: 60, content: { text: title }, style: { font_size: 52, bold: true, color: "#FFFFFF", line_height: 1.02 } },
    { type: "text", x: 20, y: 214, w: 150, h: 14, content: { text: sub }, style: { font_size: 14, color: "#94A3B8" } },
    { type: "placeholder", x: 20, y: 262, w: 150, h: 10, content: { text: "{{client.company_name}}" }, style: { font_size: 15, bold: true, color: "#FFFFFF" } },
  ] }),
  bar: (title, sub) => ({ bg_color: "#0B1A2F", elements: [
    { type: "heading", x: 20, y: 120, w: 170, h: 34, content: { text: title }, style: { font_size: 38, bold: true, color: "#FFFFFF", line_height: 1.05 } },
    { type: "shape", x: 20, y: 162, w: 60, h: 3, style: { fill: "#2EA3FD", opacity: 1 } },
    { type: "text", x: 20, y: 172, w: 170, h: 12, content: { text: sub }, style: { font_size: 13, color: "#94A3B8" } },
  ] }),
  centered: (title, sub) => ({ bg_color: "#0B1A2F", elements: [
    { type: "heading", x: 20, y: 120, w: 170, h: 36, content: { text: title }, style: { font_size: 38, bold: true, color: "#FFFFFF", align: "center", line_height: 1.05 } },
    { type: "text", x: 20, y: 164, w: 170, h: 12, content: { text: sub }, style: { font_size: 13, color: "#94A3B8", align: "center" } },
  ] }),
  block: (title, sub) => ({ bg_color: "#0B1A2F", elements: [
    { type: "shape", x: 0, y: 98, w: 210, h: 74, style: { fill: "#2EA3FD", opacity: 0.18 } },
    { type: "heading", x: 20, y: 118, w: 170, h: 36, content: { text: title }, style: { font_size: 40, bold: true, color: "#FFFFFF", line_height: 1.05 } },
    { type: "text", x: 20, y: 156, w: 170, h: 12, content: { text: sub }, style: { font_size: 13, color: "#E2E8F0" } },
  ] }),
  split: (title, sub) => ({ bg_color: "#FFFFFF", elements: [
    { type: "shape", x: 0, y: 0, w: 80, h: 297, style: { fill: "#0B1A2F", opacity: 1 } },
    { type: "heading", x: 92, y: 110, w: 100, h: 42, content: { text: title }, style: { font_size: 34, bold: true, color: "#0B1A2F", line_height: 1.06 } },
    { type: "shape", x: 92, y: 156, w: 40, h: 3, style: { fill: "#2EA3FD", opacity: 1 } },
    { type: "text", x: 92, y: 166, w: 96, h: 24, content: { text: sub }, style: { font_size: 12, color: "#475569" } },
  ] }),
};
const PRESET_LIST = [["hero", "Hero"], ["bar", "Accent bar"], ["centered", "Centered"], ["block", "Panel"], ["split", "Split"]];

export default function DesignCanvas({ page, onChange, sampleView = true }) {
  const els = page.elements || [];
  const [selId, setSelId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [pick, setPick] = useState(null); // element id awaiting image
  const [showSample, setShowSample] = useState(sampleView);
  const [libOpen, setLibOpen] = useState(false);
  const [libItems, setLibItems] = useState([]);
  const nodes = useRef({});
  const wrapRef = useRef(null);

  const sel = els.find((e) => e.id === selId) || null;
  const setEls = (next) => onChange({ elements: next });
  const patchEl = (id, patch) => setEls(els.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const patchStyle = (id, patch) => setEls(els.map((e) => (e.id === id ? { ...e, style: { ...(e.style || {}), ...patch } } : e)));
  const patchContent = (id, patch) => setEls(els.map((e) => (e.id === id ? { ...e, content: { ...(e.content || {}), ...patch } } : e)));

  // Undo / redo history for this page's layout
  const [hist, setHist] = useState({ past: [], future: [] });
  const lastSnap = useRef(null);
  const skipHist = useRef(false);
  useEffect(() => {
    const s = JSON.stringify({ elements: page.elements || [], config: page.config || {} });
    if (lastSnap.current === null) { lastSnap.current = s; return; }
    if (s === lastSnap.current) return;
    if (skipHist.current) { skipHist.current = false; lastSnap.current = s; return; }
    setHist((h) => ({ past: [...h.past, lastSnap.current].slice(-60), future: [] }));
    lastSnap.current = s;
  }, [page.elements, page.config]);
  const undo = () => {
    if (!hist.past.length) return;
    const cur = lastSnap.current, prev = hist.past[hist.past.length - 1];
    skipHist.current = true; lastSnap.current = prev; setSelId(null);
    onChange(JSON.parse(prev));
    setHist((h) => ({ past: h.past.slice(0, -1), future: [...h.future, cur] }));
  };
  const redo = () => {
    if (!hist.future.length) return;
    const cur = lastSnap.current, next = hist.future[hist.future.length - 1];
    skipHist.current = true; lastSnap.current = next; setSelId(null);
    onChange(JSON.parse(next));
    setHist((h) => ({ past: [...h.past, cur], future: h.future.slice(0, -1) }));
  };
  useEffect(() => {
    const onKey = (e) => {
      if (editingId) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); }
      else if ((e.ctrlKey || e.metaKey) && k === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hist, editingId]);

  const presetThumbEls = (variant) => (PRESET_DEFS[variant] ? PRESET_DEFS[variant](page.config?.title || "SECTION", "Subtitle").elements : []);
  const applyPreset = async (variant) => {
    if ((page.elements || []).length && !(await confirmDialog({ title: "Replace layout?", description: "Replace the current layout with this starter design? Your current elements will be removed.", confirmText: "Replace" }))) return;
    const def = PRESET_DEFS[variant] && PRESET_DEFS[variant](page.config?.title || "SECTION", "{{client.company_name}}");
    if (!def) return;
    const elements = def.elements.map((e, i) => ({ id: uid(), rotation: 0, z: i, ...e }));
    onChange({ config: { ...(page.config || {}), bg_color: def.bg_color }, elements });
    setSelId(null);
  };

  const addEl = (type) => {
    const maxZ = els.reduce((m, e) => Math.max(m, e.z || 0), 0);
    const e = { id: uid(), z: type === "background" ? -1 : maxZ + 1, rotation: 0, ...NEW[type]() };
    setEls([...els, e]); setSelId(e.id);
    if (type === "image" || type === "logo" || type === "background") setPick(e.id);
  };
  const dup = () => { if (!sel) return; const c = { ...JSON.parse(JSON.stringify(sel)), id: uid(), x: (sel.x || 0) + 5, y: (sel.y || 0) + 5, z: (els.reduce((m, e) => Math.max(m, e.z || 0), 0)) + 1 }; setEls([...els, c]); setSelId(c.id); };
  const del = () => { if (!sel) return; setEls(els.filter((e) => e.id !== sel.id)); setSelId(null); };
  const bringFront = () => { if (!sel) return; patchEl(sel.id, { z: els.reduce((m, e) => Math.max(m, e.z || 0), 0) + 1 }); };
  const sendBack = () => { if (!sel) return; patchEl(sel.id, { z: els.reduce((m, e) => Math.min(m, e.z || 0), 0) - 1 }); };

  // page-relative alignment
  const align = (mode) => {
    if (!sel) return;
    const p = { ...sel };
    if (mode === "left") p.x = 0;
    if (mode === "hcenter") p.x = (A4.w - (sel.w || 0)) / 2;
    if (mode === "right") p.x = A4.w - (sel.w || 0);
    if (mode === "top") p.y = 0;
    if (mode === "vcenter") p.y = (A4.h - (sel.h || 0)) / 2;
    if (mode === "bottom") p.y = A4.h - (sel.h || 0);
    patchEl(sel.id, { x: Math.round(p.x * 10) / 10, y: Math.round(p.y * 10) / 10 });
  };

  const guidelineNodes = useMemo(() => els.filter((e) => e.id !== selId).map((e) => nodes.current[e.id]).filter(Boolean), [els, selId]);

  // Reusable blocks — save current page layout to the content library and insert saved blocks.
  const saveLayout = async () => {
    if (!els.length) { toast?.error?.("Nothing to save yet"); return; }
    const name = (await promptDialog({ title: "Save layout", label: "Layout name", defaultValue: "My block", confirmText: "Save" })) || "";
    if (!name.trim()) return;
    try {
      const payload = { category: "design", kind: "design", name: name.trim(), elements: els };
      await api.post("/content-library", payload);
      toast?.success?.("Saved to library");
    } catch (e) {
      toast?.error?.("Save failed");
    }
  };
  const openLib = async () => {
    try {
      const r = await api.get("/content-library", { params: { category: "design" } });
      setLibItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setLibItems([]);
    }
    setLibOpen(true);
  };
  const insertBlock = (it) => {
    const src = Array.isArray(it?.elements) ? it.elements : [];
    if (!src.length) { setLibOpen(false); return; }
    const maxZ = els.reduce((m, e) => Math.max(m, e.z || 0), 0);
    const copies = src.map((e, i) => ({ ...JSON.parse(JSON.stringify(e)), id: uid(), z: maxZ + 1 + i }));
    setEls([...els, ...copies]);
    setLibOpen(false);
  };

  const displayText = (e) => {
    let t = (e.content?.text) || "";
    if (showSample) Object.entries(SAMPLE).forEach(([k, v]) => { t = t.split(`{{${k}}}`).join(v); });
    return t;
  };

  const elStyle = (e) => {
    const base = { position: "absolute", left: (e.x || 0) * PXMM, top: (e.y || 0) * PXMM, width: (e.w || 0) * PXMM, height: (e.h || 0) * PXMM, transform: `rotate(${e.rotation || 0}deg)`, opacity: e.hidden ? 0.25 : 1, cursor: "move", boxSizing: "border-box" };
    const s = e.style || {};
    if (["text", "heading", "placeholder", "page_number"].includes(e.type)) {
      return { ...base, fontSize: fontPx(s.font_size), fontWeight: s.bold ? 700 : 400, fontStyle: s.italic ? "italic" : "normal", color: s.color || "#0B1A2F", textAlign: s.align || "left", lineHeight: s.line_height || 1.3, whiteSpace: "pre-wrap", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: s.valign === "middle" ? "center" : s.valign === "bottom" ? "flex-end" : "flex-start" };
    }
    if (e.type === "shape") return { ...base, background: s.fill || "#2EA3FD", opacity: (e.hidden ? 0.25 : (s.opacity ?? 1)), borderRadius: (s.radius || 0) * PXMM, border: s.border_width ? `${s.border_width}px solid ${s.border_color || "#0B1A2F"}` : "none" };
    if (e.type === "divider") return { ...base, height: Math.max((e.h || 2) * PXMM, 2), display: "flex", alignItems: "center" };
    if (e.type === "table") return { ...base, overflow: "hidden", background: "#fff", border: "1px solid #E2E8F0" };
    return base; // image/logo/background
  };

  const imgUrl = (e) => (e.content?.url || (e.content?.image_path ? `${process.env.REACT_APP_BACKEND_URL}/api/public/files/${e.content.image_path}` : ""));

  const AlignBtn = ({ mode, icon: Icon, title }) => (
    <button title={title} onClick={() => align(mode)} className="p-1.5 rounded hover:bg-cass-bone text-cass-slate" data-testid={`align-${mode}`}><Icon className="w-4 h-4" /></button>
  );

  return (
    <div data-testid="design-canvas">
      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-1 mb-3 border-b border-border pb-3">
        {TOOLS.map(([k, l, Icon]) => (
          <Button key={k} size="sm" variant="outline" onClick={() => addEl(k)} data-testid={`add-el-${k}`}><Icon className="w-4 h-4 mr-1" />{l}</Button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={undo} disabled={!hist.past.length} title="Undo (Ctrl+Z)" data-testid="undo-btn"><Undo2 className="w-4 h-4 mr-1" />Undo</Button>
          <Button size="sm" variant="outline" onClick={redo} disabled={!hist.future.length} title="Redo (Ctrl+Shift+Z)" data-testid="redo-btn"><Redo2 className="w-4 h-4 mr-1" />Redo</Button>
          <span className="w-px h-4 bg-border mx-1" />
          <span className="text-xs text-cass-slate">Presets:</span>
          <Button size="sm" variant="outline" onClick={() => applyPreset("bar")} data-testid="canvas-preset-bar">Bar</Button>
          <Button size="sm" variant="outline" onClick={() => applyPreset("centered")} data-testid="canvas-preset-centered">Centered</Button>
          <Button size="sm" variant="outline" onClick={() => applyPreset("block")} data-testid="canvas-preset-block">Panel</Button>
          <Button size="sm" variant="outline" onClick={saveLayout} data-testid="save-layout-btn"><Save className="w-4 h-4 mr-1" />Save layout</Button>
          <Button size="sm" variant="outline" onClick={openLib} data-testid="insert-block-btn"><FolderOpen className="w-4 h-4 mr-1" />Blocks</Button>
          <label className="flex items-center gap-1 text-xs text-cass-slate"><input type="checkbox" checked={showSample} onChange={(e) => setShowSample(e.target.checked)} data-testid="toggle-sample" />Sample data</label>
        </div>
      </div>

      <div className="flex gap-4">
        {/* canvas */}
        <div className="shrink-0">
          <div className="flex items-center gap-1 mb-2">
            <span className="text-xs text-cass-slate mr-1">Align to page:</span>
            <AlignBtn mode="left" icon={AlignHorizontalJustifyStart} title="Left" />
            <AlignBtn mode="hcenter" icon={AlignHorizontalJustifyCenter} title="Centre H" />
            <AlignBtn mode="right" icon={AlignHorizontalJustifyEnd} title="Right" />
            <span className="w-px h-4 bg-border mx-1" />
            <AlignBtn mode="top" icon={AlignVerticalJustifyStart} title="Top" />
            <AlignBtn mode="vcenter" icon={AlignVerticalJustifyCenter} title="Centre V" />
            <AlignBtn mode="bottom" icon={AlignVerticalJustifyEnd} title="Bottom" />
          </div>
          <div ref={wrapRef} onClick={(e) => { if (e.target === wrapRef.current) setSelId(null); }}
            className="relative bg-white shadow-lg border border-border overflow-hidden"
            style={{ width: CW, height: CH, background: page.config?.bg_color || "#FFFFFF" }} data-testid="a4-canvas">
            {[...els].sort((a, b) => (a.z || 0) - (b.z || 0)).map((e) => (
              <div key={e.id} ref={(n) => { if (n) nodes.current[e.id] = n; }} style={elStyle(e)} data-testid={`el-${e.id}`}
                onMouseDown={(ev) => { ev.stopPropagation(); if (editingId !== e.id) setSelId(e.id); }}
                onDoubleClick={() => { if (["text", "heading", "placeholder", "page_number"].includes(e.type)) setEditingId(e.id); }}>
                {["image", "logo", "background"].includes(e.type)
                  ? (imgUrl(e) ? <img src={imgUrl(e)} alt="" style={{ width: "100%", height: "100%", objectFit: (e.style?.fit === "fill" ? "fill" : "contain"), opacity: e.style?.opacity ?? 1 }} draggable={false} /> : <div className="w-full h-full grid place-items-center text-[10px] text-cass-slate bg-slate-100 border border-dashed border-slate-300">image</div>)
                  : e.type === "table" ? <TablePreview e={e} />
                    : e.type === "shape" ? null
                    : e.type === "divider" ? <div style={{ width: "100%", borderTop: `${e.style?.thickness || 1}px solid ${e.style?.color || "#CBD5E1"}` }} />
                      : editingId === e.id
                        ? <textarea autoFocus defaultValue={e.content?.text || ""} onBlur={(ev) => { patchContent(e.id, { text: ev.target.value }); setEditingId(null); }}
                          className="w-full h-full bg-transparent resize-none outline-none border border-cass-teal" style={{ font: "inherit", color: "inherit", textAlign: "inherit", lineHeight: "inherit" }} data-testid={`edit-${e.id}`} />
                        : <span className="w-full">{displayText(e) || "\u00A0"}</span>}
              </div>
            ))}
            {els.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4" data-testid="preset-gallery">
                <div className="text-xs font-semibold text-cass-slate uppercase tracking-wide">Start with a layout</div>
                <div className="flex flex-wrap gap-4 justify-center">
                  {PRESET_LIST.map(([k, l]) => (
                    <button key={k} type="button" onClick={() => applyPreset(k)} className="flex flex-col items-center gap-1.5 group" data-testid={`gallery-preset-${k}`}>
                      <div className="rounded ring-1 ring-border group-hover:ring-2 group-hover:ring-cass-teal transition-all shadow-sm">
                        <BlockThumb elements={presetThumbEls(k)} bg={(PRESET_DEFS[k] && PRESET_DEFS[k]("", "").bg_color) || "#fff"} />
                      </div>
                      <span className="text-[11px] font-medium text-cass-slate group-hover:text-cass-navy">{l}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-cass-slate">or add elements from the toolbar above</p>
              </div>
            )}
            {sel && !sel.locked && editingId == null && nodes.current[sel.id] && (
              <Moveable
                target={nodes.current[sel.id]} draggable resizable rotatable
                origin={false} throttleDrag={0} throttleResize={0} throttleRotate={0}
                snappable snapThreshold={5} snapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
                elementSnapDirections={{ top: true, left: true, bottom: true, right: true, center: true, middle: true }}
                elementGuidelines={guidelineNodes}
                verticalGuidelines={[0, CW / 2, CW]} horizontalGuidelines={[0, CH / 2, CH]}
                bounds={{ left: 0, top: 0, right: CW, bottom: CH }}
                onDrag={({ target, left, top }) => { target.style.left = `${left}px`; target.style.top = `${top}px`; }}
                onDragEnd={({ target }) => patchEl(sel.id, { x: Math.round(parseFloat(target.style.left) / PXMM * 10) / 10, y: Math.round(parseFloat(target.style.top) / PXMM * 10) / 10 })}
                onResize={({ target, width, height, drag }) => { target.style.width = `${width}px`; target.style.height = `${height}px`; target.style.left = `${drag.left}px`; target.style.top = `${drag.top}px`; }}
                onResizeEnd={({ target }) => patchEl(sel.id, { w: Math.round(parseFloat(target.style.width) / PXMM * 10) / 10, h: Math.round(parseFloat(target.style.height) / PXMM * 10) / 10, x: Math.round(parseFloat(target.style.left) / PXMM * 10) / 10, y: Math.round(parseFloat(target.style.top) / PXMM * 10) / 10 })}
                onRotate={({ target, rotation }) => { target.style.transform = `rotate(${rotation}deg)`; }}
                onRotateEnd={({ target }) => { const m = /rotate\(([-0-9.]+)deg\)/.exec(target.style.transform); patchEl(sel.id, { rotation: m ? Math.round(parseFloat(m[1])) : 0 }); }}
              />
            )}
          </div>
        </div>

        {/* properties */}
        <div className="flex-1 min-w-[240px] space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-cass-slate uppercase">{sel ? sel.type : "Page"}</span>
            {sel && <>
              <button title="Duplicate" onClick={dup} className="p-1.5 rounded hover:bg-cass-bone" data-testid="el-dup"><Copy className="w-4 h-4" /></button>
              <button title="Bring to front" onClick={bringFront} className="p-1.5 rounded hover:bg-cass-bone" data-testid="el-front"><BringToFront className="w-4 h-4" /></button>
              <button title="Send to back" onClick={sendBack} className="p-1.5 rounded hover:bg-cass-bone" data-testid="el-back"><SendToBack className="w-4 h-4" /></button>
              <button title={sel.locked ? "Unlock" : "Lock"} onClick={() => patchEl(sel.id, { locked: !sel.locked })} className="p-1.5 rounded hover:bg-cass-bone" data-testid="el-lock">{sel.locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}</button>
              <button title={sel.hidden ? "Show" : "Hide"} onClick={() => patchEl(sel.id, { hidden: !sel.hidden })} className="p-1.5 rounded hover:bg-cass-bone" data-testid="el-hide">{sel.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              <button title="Delete" onClick={del} className="p-1.5 rounded hover:bg-cass-bone text-red-500" data-testid="el-del"><Trash2 className="w-4 h-4" /></button>
            </>}
          </div>

          {!sel && (
            <div className="space-y-2">
              <div><Label className="text-xs">Page background colour</Label><Input type="color" value={page.config?.bg_color || "#FFFFFF"} onChange={(e) => onChange({ config: { ...(page.config || {}), bg_color: e.target.value } })} className="h-9 p-1" data-testid="page-bg-color" /></div>
              <p className="text-xs text-cass-slate">Select an element to edit it, or add one from the toolbar. Positions are in millimetres on an A4 page.</p>
            </div>
          )}

          {sel && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                {[["x", "X"], ["y", "Y"], ["w", "W"], ["h", "H"]].map(([k, l]) => (
                  <div key={k}><Label className="text-[11px]">{l} (mm)</Label><Input type="number" value={Math.round((sel[k] || 0) * 10) / 10} onChange={(e) => patchEl(sel.id, { [k]: parseFloat(e.target.value) || 0 })} className="h-8" data-testid={`prop-${k}`} /></div>
                ))}
              </div>
              <div><Label className="text-[11px]">Rotation (°)</Label><Input type="number" value={sel.rotation || 0} onChange={(e) => patchEl(sel.id, { rotation: parseFloat(e.target.value) || 0 })} className="h-8" data-testid="prop-rot" /></div>

              {["text", "heading", "placeholder", "page_number"].includes(sel.type) && (
                <div className="space-y-2 border-t border-border pt-3">
                  {sel.type === "placeholder" && (
                    <div><Label className="text-[11px]">Placeholder</Label>
                      <select className="w-full h-8 border rounded text-sm px-2" value={sel.content?.text || ""} onChange={(e) => patchContent(sel.id, { text: e.target.value })} data-testid="prop-placeholder">
                        {PLACEHOLDERS.map(([code, k]) => <option key={code} value={code}>{k}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[11px]">Font size (pt)</Label><Input type="number" value={sel.style?.font_size || 12} onChange={(e) => patchStyle(sel.id, { font_size: parseFloat(e.target.value) || 12 })} className="h-8" data-testid="prop-fontsize" /></div>
                    <div><Label className="text-[11px]">Colour</Label><Input type="color" value={sel.style?.color || "#0B1A2F"} onChange={(e) => patchStyle(sel.id, { color: e.target.value })} className="h-8 p-1" data-testid="prop-color" /></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => patchStyle(sel.id, { bold: !sel.style?.bold })} className={`px-2 py-1 rounded border text-sm font-bold ${sel.style?.bold ? "bg-cass-navy text-white" : ""}`} data-testid="prop-bold">B</button>
                    <button onClick={() => patchStyle(sel.id, { italic: !sel.style?.italic })} className={`px-2 py-1 rounded border text-sm italic ${sel.style?.italic ? "bg-cass-navy text-white" : ""}`} data-testid="prop-italic">I</button>
                    <select className="h-8 border rounded text-sm px-2" value={sel.style?.align || "left"} onChange={(e) => patchStyle(sel.id, { align: e.target.value })} data-testid="prop-align">
                      <option value="left">Left</option><option value="center">Centre</option><option value="right">Right</option><option value="justify">Justify</option>
                    </select>
                    <select className="h-8 border rounded text-sm px-2" value={sel.style?.valign || "top"} onChange={(e) => patchStyle(sel.id, { valign: e.target.value })} data-testid="prop-valign">
                      <option value="top">Top</option><option value="middle">Middle</option><option value="bottom">Bottom</option>
                    </select>
                  </div>
                </div>
              )}

              {["image", "logo", "background"].includes(sel.type) && (
                <div className="space-y-2 border-t border-border pt-3">
                  <Button size="sm" variant="outline" onClick={() => setPick(sel.id)} data-testid="prop-choose-image"><ImageIcon className="w-4 h-4 mr-1" />Choose image</Button>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[11px]">Fit</Label><select className="w-full h-8 border rounded text-sm px-2" value={sel.style?.fit || "contain"} onChange={(e) => patchStyle(sel.id, { fit: e.target.value })} data-testid="prop-fit"><option value="contain">Contain</option><option value="fill">Fill</option></select></div>
                    <div><Label className="text-[11px]">Opacity</Label><Input type="number" step="0.1" min="0" max="1" value={sel.style?.opacity ?? 1} onChange={(e) => patchStyle(sel.id, { opacity: parseFloat(e.target.value) })} className="h-8" data-testid="prop-opacity" /></div>
                  </div>
                </div>
              )}

              {sel.type === "shape" && (
                <div className="space-y-2 border-t border-border pt-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[11px]">Fill</Label><Input type="color" value={sel.style?.fill || "#2EA3FD"} onChange={(e) => patchStyle(sel.id, { fill: e.target.value })} className="h-8 p-1" data-testid="prop-fill" /></div>
                    <div><Label className="text-[11px]">Opacity</Label><Input type="number" step="0.1" min="0" max="1" value={sel.style?.opacity ?? 1} onChange={(e) => patchStyle(sel.id, { opacity: parseFloat(e.target.value) })} className="h-8" /></div>
                    <div><Label className="text-[11px]">Corner radius (mm)</Label><Input type="number" value={sel.style?.radius || 0} onChange={(e) => patchStyle(sel.id, { radius: parseFloat(e.target.value) || 0 })} className="h-8" data-testid="prop-radius" /></div>
                    <div><Label className="text-[11px]">Border width</Label><Input type="number" value={sel.style?.border_width || 0} onChange={(e) => patchStyle(sel.id, { border_width: parseFloat(e.target.value) || 0 })} className="h-8" /></div>
                  </div>
                </div>
              )}

              {sel.type === "divider" && (
                <div className="grid grid-cols-2 gap-2 border-t border-border pt-3">
                  <div><Label className="text-[11px]">Colour</Label><Input type="color" value={sel.style?.color || "#CBD5E1"} onChange={(e) => patchStyle(sel.id, { color: e.target.value })} className="h-8 p-1" /></div>
                  <div><Label className="text-[11px]">Thickness</Label><Input type="number" value={sel.style?.thickness || 1} onChange={(e) => patchStyle(sel.id, { thickness: parseFloat(e.target.value) || 1 })} className="h-8" /></div>
                </div>
              )}

              {sel.type === "table" && (
                <div className="space-y-2 border-t border-border pt-3">
                  <div><Label className="text-[11px]">Data source</Label>
                    <select className="w-full h-8 border rounded text-sm px-2" value={sel.content?.source || "monthly"} onChange={(e) => patchContent(sel.id, { source: e.target.value })} data-testid="prop-table-source">
                      {TABLE_SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[11px]">Header bg</Label><Input type="color" value={sel.style?.header_bg || "#0B1A2F"} onChange={(e) => patchStyle(sel.id, { header_bg: e.target.value })} className="h-8 p-1" data-testid="prop-table-headerbg" /></div>
                    <div><Label className="text-[11px]">Header text</Label><Input type="color" value={sel.style?.header_fg || "#FFFFFF"} onChange={(e) => patchStyle(sel.id, { header_fg: e.target.value })} className="h-8 p-1" /></div>
                    <div><Label className="text-[11px]">Alt row</Label><Input type="color" value={sel.style?.alt_row || "#F1F5F9"} onChange={(e) => patchStyle(sel.id, { alt_row: e.target.value })} className="h-8 p-1" /></div>
                    <div><Label className="text-[11px]">Font size (pt)</Label><Input type="number" value={sel.style?.font_size || 9} onChange={(e) => patchStyle(sel.id, { font_size: parseFloat(e.target.value) || 9 })} className="h-8" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-cass-slate"><input type="checkbox" checked={sel.style?.show_totals !== false} onChange={(e) => patchStyle(sel.id, { show_totals: e.target.checked })} data-testid="prop-table-totals" />Show totals row</label>
                  <p className="text-[11px] text-cass-slate">Pulls live from the proposal and flows onto extra pages automatically.</p>
                </div>
              )}
            </div>
          )}

          {/* layers */}
          {els.length > 0 && (
            <div className="border-t border-border pt-3">
              <div className="text-[11px] font-semibold text-cass-slate uppercase mb-1 flex items-center gap-1"><Layers className="w-3.5 h-3.5" />Layers</div>
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {[...els].sort((a, b) => (b.z || 0) - (a.z || 0)).map((e) => (
                  <div key={e.id} onClick={() => setSelId(e.id)} className={`text-xs px-2 py-1 rounded cursor-pointer truncate ${selId === e.id ? "bg-cass-teal/10 text-cass-navy" : "hover:bg-cass-bone text-cass-slate"}`} data-testid={`layer-${e.id}`}>
                    {e.type}{e.content?.text ? ` — ${e.content.text.slice(0, 24)}` : ""}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ImagePicker open={pick !== null} onOpenChange={(o) => !o && setPick(null)}
        onSelect={(path, url) => { if (pick) patchContent(pick, { image_path: path, url }); setPick(null); }} />

      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent data-testid="lib-dialog">
          <DialogHeader><DialogTitle>Insert a saved block</DialogTitle></DialogHeader>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {libItems.length === 0 && <p className="text-sm text-cass-slate">No saved blocks yet. Use "Save layout" to create one.</p>}
            {libItems.map((it) => (
              <button key={it.id} onClick={() => insertBlock(it)} className="w-full text-left px-3 py-2 rounded hover:bg-cass-bone text-sm flex items-center gap-3" data-testid={`lib-item-${it.id}`}>
                <BlockThumb elements={it.elements || []} />
                <span className="flex-1">{it.name}</span><span className="text-xs text-cass-slate">{(it.elements || []).length} elements</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
